import React, { FC, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

type TagStyle =
  | 'danger'
  | 'offline'
  | 'gesloten'
  | 'closed'
  | 'bussy'
  | 'in behandeling'
  | 'in uitvoering'
  | 'toegewezen'
  | 'lopende'
  | 'success'
  | 'online'
  | 'open'
  | 'nieuw'
  | 'todo'
  | 'new'
  | 'muted'
  | 'hover'
  | 'blue'
  | 'default'
  | 'cyod'
  | 'neutral'
  | 'neutralcolored'
  | 'important'
  | 'it'
  | 'telecom'
  | 'backoffice'
  | 'development'
  | 'sales'
  | 'management'
  | 'high'
  | 'urgent'
  | 'pending'
  | 'active'
  | 'blocked'
  | 'owner'
  | 'user'
  | 'admin';

type Props = {
  style?: TagStyle | null;
  label?: string | ReactNode | null;
  icon?: ReactNode;
  lowercase?: boolean;
  extraClasses?: string;
};

const tagStyles = {
  // Error/Warning
  danger: 'border border-red-700 bg-red-100 text-red-700 px-2',
  offline: 'border border-red-700 bg-red-100 text-red-700 px-2',
  gesloten: 'bg-red-100 text-red-700 px-2',
  closed: 'bg-red-100 text-red-700 px-2',
  important: 'bg-red-100 text-red-700 px-2',
  urgent: 'bg-red-100 text-red-700 px-2',
  blocked: 'bg-red-100 text-red-700 px-2',

  // Warning/In Progress
  bussy: 'bg-orange-50 text-orange-400 px-2',
  high: 'bg-orange-50 text-orange-400 px-2',
  'in behandeling': 'bg-orange-50 text-orange-400 px-2',
  'in uitvoering': 'bg-orange-50 text-orange-400 px-2',
  toegewezen: 'bg-orange-50 text-orange-400 px-2',
  lopende: 'bg-orange-50 text-orange-400 px-2',
  pending: 'bg-orange-50 text-orange-400 px-2',

  // Success
  success: 'bg-green-100 text-green-700 px-2',
  online: 'bg-green-100 text-green-700 px-2',
  open: 'bg-green-50 text-green-700 px-2 border border-green-700',
  nieuw: 'bg-green-50 text-green-700 px-2',
  todo: 'bg-green-50 text-green-700 px-2',
  new: 'bg-green-50 text-green-700 px-2 border-green-700',
  active: 'bg-green-50 text-green-700 px-2 border-green-700',

  // Neutral/Muted
  muted: 'bg-slate-400 bg-opacity-10 px-2',
  hover:
    'bg-slate-400 bg-opacity-10 px-2 hover:bg-opacity-50 hover:text-slate-700',
  neutral: 'normal-case font-regular px-2',
  neutralcolored: 'bg-blue-100 text-blue-700 px-2',

  // Custom
  blue: 'border border-blue-700 text-blue-700 hover:text-white hover:bg-blue-700 dark:border-gray-700 dark:hover:border-blue-700 dark:text-white px-2',
  default:
    'border border-blue-700 text-blue-700 dark:border-gray-700 dark:hover:border-blue-700 dark:text-white px-2',
  cyod: 'border border-blue-700 text-white bg-blue-700 dark:border-gray-700 px-2',

  // Department/Category
  it: 'bg-purple-100 text-purple-700 px-2',
  owner: 'bg-purple-100 text-purple-700 px-2',
  telecom: 'bg-purple-100 text-purple-700 px-2',
  backoffice: 'bg-green-100 text-green-700 px-2',
  development: 'bg-yellow-100 text-yellow-700 px-2',
  user: 'bg-teal-100 text-teal-700 px-2',
  sales: 'bg-teal-100 text-teal-700 px-2',
  management: 'bg-indigo-100 text-indigo-700 px-2',
  admin: 'bg-indigo-100 text-indigo-700 px-2',
} as const;

export const Tag: FC<Props> = React.memo(
  ({ style = 'default', label, icon, lowercase, extraClasses }) => {
    const { t } = useTranslation();
    const styleModified = style?.toLowerCase() as keyof typeof tagStyles;

    const classNames = [
      'inline-flex items-center gap-1 rounded-md leading-none h-5 px-2',
      !lowercase && 'uppercase',
      tagStyles[styleModified],
      extraClasses,
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <span className={classNames}>
        {icon} {typeof label === 'string' ? t(label) : label}
      </span>
    );
  }
);

export default Tag;
